import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAudience } from '../context/AudienceContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Calculator,
  Copy,
  Scale,
  Baby,
  Syringe,
  AlertTriangle,
  Check,
  Loader2,
  Brain,
  Droplet,
  Clock,
  Activity,
  Info
} from 'lucide-react';

import { API_URL } from '../config';

const routes = ['IV', 'IM', 'SC', 'PO', 'IR', 'IN', 'Nébulisation', 'IVSE'];
const timeUnits = ['Heure(s)', 'Minute(s)'];

export default function DoseCalculatorPage() {
  const { token } = useAuth();
  const { audience, isAdult } = useAudience();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('medication');
  
  // Medication calculator state
  const [medicationName, setMedicationName] = useState('');
  const [weight, setWeight] = useState(isAdult ? 70 : 15);
  const [age, setAge] = useState(isAdult ? 40 : 5);
  const [route, setRoute] = useState('IV');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  // Flow rate calculator (µg/kg/min → ml/h)
  const [flowPrescription, setFlowPrescription] = useState('');
  const [flowWeight, setFlowWeight] = useState('70');
  const [flowDoseAvailable, setFlowDoseAvailable] = useState('');
  const [flowVolumeAvailable, setFlowVolumeAvailable] = useState('');
  const [flowResult, setFlowResult] = useState(null);

  // Pump rate calculator (ml/h)
  const [pumpVolume, setPumpVolume] = useState('');
  const [pumpTime, setPumpTime] = useState('');
  const [pumpTimeUnit, setPumpTimeUnit] = useState('Heure(s)');
  const [pumpResult, setPumpResult] = useState(null);

  // IV dose calculator
  const [ivDosePrescribed, setIvDosePrescribed] = useState('');
  const [ivDoseAvailable, setIvDoseAvailable] = useState('');
  const [ivVolumeAvailable, setIvVolumeAvailable] = useState('');
  const [ivResult, setIvResult] = useState(null);

  useEffect(() => {
    setWeight(isAdult ? 70 : 15);
    setAge(isAdult ? 40 : 5);
    setFlowWeight(isAdult ? '70' : '15');
  }, [isAdult]);

  const searchMedications = async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/medications?query=${encodeURIComponent(query)}&audience=${audience}&limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.medications || []);
      }
    } catch (error) {
      console.error('Error searching medications:', error);
    }
  };

  const calculateDose = async () => {
    if (!medicationName || !weight) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(`${API_URL}/api/dose/calc`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          medication_name: medicationName,
          weight: parseFloat(weight),
          age: parseInt(age),
          audience,
          route
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
      } else {
        toast.error('Erreur lors du calcul');
      }
    } catch (error) {
      console.error('Error calculating dose:', error);
      toast.error('Erreur lors du calcul');
    } finally {
      setLoading(false);
    }
  };

  // Calculate flow rate (µg/kg/min → ml/h)
  const calculateFlowRate = () => {
    const prescription = parseFloat(flowPrescription);
    const weightKg = parseFloat(flowWeight);
    const doseMg = parseFloat(flowDoseAvailable);
    const volumeMl = parseFloat(flowVolumeAvailable);

    if (!prescription || !weightKg || !doseMg || !volumeMl) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    // Formula: (prescription µg/kg/min × weight kg × 60 min × volume ml) / (dose mg × 1000)
    const flowRateMlH = (prescription * weightKg * 60 * volumeMl) / (doseMg * 1000);
    
    setFlowResult({
      value: flowRateMlH.toFixed(2),
      unit: 'ml/h',
      formula: `(${prescription} × ${weightKg} × 60 × ${volumeMl}) / (${doseMg} × 1000) = ${flowRateMlH.toFixed(2)} ml/h`
    });
    toast.success('Calcul effectué');
  };

  // Calculate pump rate
  const calculatePumpRate = () => {
    const volume = parseFloat(pumpVolume);
    const time = parseFloat(pumpTime);

    if (!volume || !time) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    let timeInHours = time;
    if (pumpTimeUnit === 'Minute(s)') {
      timeInHours = time / 60;
    }

    const rate = volume / timeInHours;

    setPumpResult({
      value: rate.toFixed(1),
      unit: 'ml/h',
      formula: `${volume} ml / ${time} ${pumpTimeUnit.toLowerCase()} = ${rate.toFixed(1)} ml/h`
    });
    toast.success('Calcul effectué');
  };

  // Calculate IV dose volume
  const calculateIvDose = () => {
    const dosePrescribed = parseFloat(ivDosePrescribed);
    const doseAvailable = parseFloat(ivDoseAvailable);
    const volumeAvailable = parseFloat(ivVolumeAvailable);

    if (!dosePrescribed || !doseAvailable || !volumeAvailable) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    const volumeToAdminister = (dosePrescribed * volumeAvailable) / doseAvailable;

    setIvResult({
      value: volumeToAdminister.toFixed(2),
      unit: 'ml',
      formula: `(${dosePrescribed} × ${volumeAvailable}) / ${doseAvailable} = ${volumeToAdminister.toFixed(2)} ml`
    });
    toast.success('Calcul effectué');
  };

  const copyDose = (dose) => {
    const text = `${medicationName}: ${dose.dose} ${dose.unit} ${dose.route} - ${dose.formula}`;
    navigator.clipboard.writeText(text);
    toast.success('Posologie copiée');
  };

  const copyResult = (resultObj, type) => {
    const text = `${type}: ${resultObj.value} ${resultObj.unit} (${resultObj.formula})`;
    navigator.clipboard.writeText(text);
    toast.success('Résultat copié');
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Calculator className="w-6 h-6 text-cyan-400" />
          Calculateurs
        </h1>
        <p className="text-[hsl(var(--text-dim))]">Mode {isAdult ? 'adulte' : 'pédiatrique'}</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 bg-[hsl(var(--bg-elev))]">
          <TabsTrigger value="medication" className="text-xs md:text-sm" data-testid="calc-tab-medication">
            <Syringe className="w-4 h-4 mr-1 hidden sm:inline" />
            Dose
          </TabsTrigger>
          <TabsTrigger value="flow" className="text-xs md:text-sm" data-testid="calc-tab-flow">
            <Activity className="w-4 h-4 mr-1 hidden sm:inline" />
            Débit
          </TabsTrigger>
          <TabsTrigger value="pump" className="text-xs md:text-sm" data-testid="calc-tab-pump">
            <Droplet className="w-4 h-4 mr-1 hidden sm:inline" />
            Perfusion
          </TabsTrigger>
          <TabsTrigger value="iv" className="text-xs md:text-sm" data-testid="calc-tab-iv">
            <Syringe className="w-4 h-4 mr-1 hidden sm:inline" />
            IV
          </TabsTrigger>
        </TabsList>

        {/* Medication Dose Calculator */}
        <TabsContent value="medication" className="mt-6">
          <div className="glass-card rounded-xl p-6 space-y-6">
            <div className="flex items-center gap-2 text-cyan-400 mb-2">
              <Syringe className="w-5 h-5" />
              <h2 className="font-semibold">Calculateur de doses médicamenteuses</h2>
            </div>
            <p className="text-sm text-[hsl(var(--text-dim))]">
              Calcule la posologie selon le poids et la voie d'administration
            </p>

            {/* Medication */}
            <div className="relative">
              <label className="block text-sm font-medium text-[hsl(var(--text-dim))] mb-2">Médicament</label>
              <Input
                type="text"
                value={medicationName}
                onChange={(e) => {
                  setMedicationName(e.target.value);
                  searchMedications(e.target.value);
                }}
                placeholder="Ex: Adrénaline, Paracétamol..."
                className="bg-transparent"
                data-testid="dose-medication-input"
              />
              {suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[hsl(var(--bg-elev))] border border-[hsl(var(--border))] rounded-lg shadow-xl z-50">
                  {suggestions.map((med) => (
                    <button
                      key={med._id}
                      className="w-full text-left px-4 py-2 hover:bg-cyan-400/10 transition-colors"
                      onClick={() => {
                        setMedicationName(med.name);
                        setSuggestions([]);
                      }}
                    >
                      <p className="font-medium">{med.name}</p>
                      <p className="text-xs text-[hsl(var(--text-dim))]">{med.brand_name}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Weight */}
            <div>
              <label className="block text-sm font-medium text-[hsl(var(--text-dim))] mb-2">
                <Scale className="w-4 h-4 inline mr-1" />
                Poids: <span className="text-cyan-300 font-mono">{weight} kg</span>
              </label>
              <Slider
                value={[weight]}
                onValueChange={([val]) => setWeight(val)}
                min={isAdult ? 30 : 1}
                max={isAdult ? 150 : 80}
                step={isAdult ? 5 : 1}
                className="mt-2"
                data-testid="dose-weight-input"
              />
              <div className="flex justify-between text-xs text-[hsl(var(--muted))] mt-1">
                <span>{isAdult ? '30' : '1'} kg</span>
                <span>{isAdult ? '150' : '80'} kg</span>
              </div>
            </div>

            {/* Age (for pediatric) */}
            {!isAdult && (
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-dim))] mb-2">
                  <Baby className="w-4 h-4 inline mr-1" />
                  Âge: <span className="text-cyan-300 font-mono">{age} ans</span>
                </label>
                <Slider
                  value={[age]}
                  onValueChange={([val]) => setAge(val)}
                  min={0}
                  max={18}
                  step={1}
                  className="mt-2"
                  data-testid="dose-age-input"
                />
              </div>
            )}

            {/* Route */}
            <div>
              <label className="block text-sm font-medium text-[hsl(var(--text-dim))] mb-2">
                <Syringe className="w-4 h-4 inline mr-1" />
                Voie d'administration
              </label>
              <Select value={route} onValueChange={setRoute}>
                <SelectTrigger className="bg-transparent" data-testid="dose-route-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {routes.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Calculate Button */}
            <Button
              onClick={calculateDose}
              disabled={loading || !medicationName}
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium hover:opacity-90"
              data-testid="dose-calculate-button"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Calculator className="w-5 h-5 mr-2" />
                  Calculer
                </>
              )}
            </Button>
          </div>

          {/* Results */}
          {result && (
            <div className="space-y-4 animate-scale-in mt-6">
              {result.calculated_doses?.length > 0 ? (
                result.calculated_doses.map((dose, i) => (
                  <div key={i} className="dose-result">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-[hsl(var(--text-dim))] mb-1">{dose.indication}</p>
                        <div className="dose-value" data-testid="dose-result-value">
                          {dose.dose} {dose.unit}
                        </div>
                        <div className="flex flex-wrap gap-3 mt-2 text-sm text-[hsl(var(--text-dim))]">
                          <span className="flex items-center gap-1">
                            <Syringe className="w-4 h-4" /> {dose.route}
                          </span>
                          {dose.frequency && (
                            <span>{dose.frequency}</span>
                          )}
                        </div>
                        <p className="text-xs text-cyan-400/70 mt-2 font-mono">{dose.formula}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyDose(dose)}
                        data-testid="dose-copy-button"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              ) : result.ai_response ? (
                <div className="glass-card rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-3 text-cyan-400">
                    <Brain className="w-5 h-5" />
                    <span className="font-medium">Réponse IA</span>
                  </div>
                  <div className="prose prose-invert prose-sm max-w-none">
                    <pre className="whitespace-pre-wrap text-sm text-[hsl(var(--text))]">{result.ai_response}</pre>
                  </div>
                </div>
              ) : (
                <div className="glass-card rounded-xl p-6 text-center">
                  <p className="text-[hsl(var(--text-dim))]">Aucune posologie trouvée pour ce médicament</p>
                </div>
              )}

              {/* Warnings */}
              {result.warnings?.length > 0 && (
                <div className="glass-card rounded-xl p-4 border-amber-500/30">
                  <div className="flex items-center gap-2 text-amber-400 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="font-medium">Attention</span>
                  </div>
                  <ul className="space-y-1">
                    {result.warnings.map((warning, i) => (
                      <li key={i} className="text-sm text-amber-300">{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Flow Rate Calculator (µg/kg/min → ml/h) */}
        <TabsContent value="flow" className="mt-6">
          <div className="glass-card rounded-xl p-6 space-y-6">
            <div className="flex items-center gap-2 text-teal-400 mb-2">
              <Activity className="w-5 h-5" />
              <h2 className="font-semibold">Calcul de débit : µg/kg/min → ml/h</h2>
            </div>
            <p className="text-sm text-[hsl(var(--text-dim))]">
              Détermine le débit à afficher sur une seringue électrique ou pompe à perfusion
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-dim))] mb-2">
                  Prescription (µg/kg/min) <span className="text-red-400">*</span>
                </label>
                <Input
                  type="number"
                  step="0.1"
                  value={flowPrescription}
                  onChange={(e) => setFlowPrescription(e.target.value)}
                  placeholder="Ex: 1"
                  className="bg-transparent"
                  data-testid="flow-prescription-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-dim))] mb-2">
                  Poids (kg) <span className="text-red-400">*</span>
                </label>
                <Input
                  type="number"
                  value={flowWeight}
                  onChange={(e) => setFlowWeight(e.target.value)}
                  placeholder="Ex: 70"
                  className="bg-transparent"
                  data-testid="flow-weight-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-dim))] mb-2">
                  Dose disponible (mg) <span className="text-red-400">*</span>
                </label>
                <Input
                  type="number"
                  value={flowDoseAvailable}
                  onChange={(e) => setFlowDoseAvailable(e.target.value)}
                  placeholder="Ex: 4"
                  className="bg-transparent"
                  data-testid="flow-dose-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-dim))] mb-2">
                  Volume disponible (ml) <span className="text-red-400">*</span>
                </label>
                <Input
                  type="number"
                  value={flowVolumeAvailable}
                  onChange={(e) => setFlowVolumeAvailable(e.target.value)}
                  placeholder="Ex: 50"
                  className="bg-transparent"
                  data-testid="flow-volume-input"
                />
              </div>
            </div>

            <Button
              onClick={calculateFlowRate}
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium hover:opacity-90"
              data-testid="flow-calculate-button"
            >
              <Calculator className="w-5 h-5 mr-2" />
              Calculer
            </Button>

            {flowResult && (
              <div className="mt-4 p-4 bg-teal-500/10 border border-teal-500/30 rounded-xl animate-scale-in">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[hsl(var(--text-dim))]">Débit à afficher</p>
                    <p className="text-3xl font-bold text-teal-400 font-mono">
                      {flowResult.value} <span className="text-lg">{flowResult.unit}</span>
                    </p>
                    <p className="text-xs text-[hsl(var(--text-dim))] mt-2 font-mono">{flowResult.formula}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyResult(flowResult, 'Débit')}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Pump Rate Calculator (ml/h) */}
        <TabsContent value="pump" className="mt-6">
          <div className="glass-card rounded-xl p-6 space-y-6">
            <div className="flex items-center gap-2 text-purple-400 mb-2">
              <Droplet className="w-5 h-5" />
              <h2 className="font-semibold">Débit de pompe à perfusion (ml/h)</h2>
            </div>
            <p className="text-sm text-[hsl(var(--text-dim))]">
              Détermine le débit à afficher selon le volume et la durée
            </p>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-dim))] mb-2">
                  Volume prescrit (ml) <span className="text-red-400">*</span>
                </label>
                <Input
                  type="number"
                  value={pumpVolume}
                  onChange={(e) => setPumpVolume(e.target.value)}
                  placeholder="Ex: 1000"
                  className="bg-transparent"
                  data-testid="pump-volume-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-dim))] mb-2">
                  Temps <span className="text-red-400">*</span>
                </label>
                <Input
                  type="number"
                  value={pumpTime}
                  onChange={(e) => setPumpTime(e.target.value)}
                  placeholder="Ex: 1 ou 60"
                  className="bg-transparent"
                  data-testid="pump-time-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-dim))] mb-2">
                  Unité de temps
                </label>
                <Select value={pumpTimeUnit} onValueChange={setPumpTimeUnit}>
                  <SelectTrigger className="bg-transparent" data-testid="pump-time-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeUnits.map((unit) => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={calculatePumpRate}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium hover:opacity-90"
              data-testid="pump-calculate-button"
            >
              <Calculator className="w-5 h-5 mr-2" />
              Calculer
            </Button>

            {pumpResult && (
              <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl animate-scale-in">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[hsl(var(--text-dim))]">Débit à afficher</p>
                    <p className="text-3xl font-bold text-purple-400 font-mono">
                      {pumpResult.value} <span className="text-lg">{pumpResult.unit}</span>
                    </p>
                    <p className="text-xs text-[hsl(var(--text-dim))] mt-2 font-mono">{pumpResult.formula}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyResult(pumpResult, 'Débit perfusion')}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* IV Dose Calculator */}
        <TabsContent value="iv" className="mt-6">
          <div className="glass-card rounded-xl p-6 space-y-6">
            <div className="flex items-center gap-2 text-green-400 mb-2">
              <Syringe className="w-5 h-5" />
              <h2 className="font-semibold">Calcul de dose : Injection IV</h2>
            </div>
            <p className="text-sm text-[hsl(var(--text-dim))]">
              Détermine le volume à prélever pour une prescription IV
            </p>

            {/* Warning */}
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-300">
                  <strong>Important :</strong> Les doses prescrite et disponible doivent être dans la même unité (mg, g, UI, etc.)
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-dim))] mb-2">
                  Dose prescrite <span className="text-red-400">*</span>
                </label>
                <Input
                  type="number"
                  value={ivDosePrescribed}
                  onChange={(e) => setIvDosePrescribed(e.target.value)}
                  placeholder="Ex: 250"
                  className="bg-transparent"
                  data-testid="iv-dose-prescribed-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-dim))] mb-2">
                  Dose disponible <span className="text-red-400">*</span>
                </label>
                <Input
                  type="number"
                  value={ivDoseAvailable}
                  onChange={(e) => setIvDoseAvailable(e.target.value)}
                  placeholder="Ex: 1000"
                  className="bg-transparent"
                  data-testid="iv-dose-available-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-dim))] mb-2">
                  Volume disponible (ml) <span className="text-red-400">*</span>
                </label>
                <Input
                  type="number"
                  value={ivVolumeAvailable}
                  onChange={(e) => setIvVolumeAvailable(e.target.value)}
                  placeholder="Ex: 10"
                  className="bg-transparent"
                  data-testid="iv-volume-available-input"
                />
              </div>
            </div>

            <Button
              onClick={calculateIvDose}
              className="w-full bg-gradient-to-r from-green-500 to-teal-500 text-white font-medium hover:opacity-90"
              data-testid="iv-calculate-button"
            >
              <Calculator className="w-5 h-5 mr-2" />
              Calculer
            </Button>

            {ivResult && (
              <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-xl animate-scale-in">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[hsl(var(--text-dim))]">Volume à prélever</p>
                    <p className="text-3xl font-bold text-green-400 font-mono">
                      {ivResult.value} <span className="text-lg">{ivResult.unit}</span>
                    </p>
                    <p className="text-xs text-[hsl(var(--text-dim))] mt-2 font-mono">{ivResult.formula}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyResult(ivResult, 'Volume IV')}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Disclaimer */}
      <div className="glass-card rounded-lg p-4 border border-amber-500/30">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[hsl(var(--text-dim))]">
            Les calculs sont fournis à titre indicatif. Toujours vérifier la posologie selon le protocole local et le contexte clinique.
          </p>
        </div>
      </div>
    </div>
  );
}
